/**
 * Projekte — rein lesende Sicht auf ERPNext-Projekte des Kunden (TQ #341,
 * Auftrag marco-assistenz-workspace/auftraege/2026-09-25-cockpit-projekte-und-preisinfo.md).
 *
 * GET /api/org/:slug/projects -> { projects: Project[] }. Feldnamen von
 * platform-workspace bestaetigt (07.10.2026, main @ 91dcb35, erpnextbot.Project):
 *   name, customer, status, percent_complete (float), expected_start_date,
 *   expected_end_date. status ist der rohe ERPNext-Wert (Open|Completed|Cancelled).
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { useToast } from '../components/ui'
import { PROJECT_STATUS_LABEL } from '../lib/projectStatus'

interface Organization {
  id: number
  name: string
  slug: string
  public_token?: string
  member_role: string
}

interface Project {
  name: string
  customer: string
  status: string
  percent_complete: number
  expected_start_date?: string
  expected_end_date?: string
}

interface Props {
  organization: Organization
}

const fmtDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–'

const STATUS_LABEL = PROJECT_STATUS_LABEL

export default function Projects({ organization }: Props) {
  const { api: keycloakApi } = useAuth()
  const { showToast } = useToast()
  const slugOrToken = organization.public_token || organization.slug

  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  // Backend meldet unavailable=no_access, wenn ERPNext dem Read-Bot das Lesen von Projekten verweigert (TQ #783).
  const [unavailable, setUnavailable] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setUnavailable(false)
      try {
        const r = await keycloakApi!.get(`/api/org/${slugOrToken}/projects`)
        if (cancelled) return
        const list = r.data?.projects ?? []
        setProjects(Array.isArray(list) ? list : [])
        setUnavailable(r.data?.unavailable === 'no_access')
      } catch (e) {
        if (cancelled) return
        const err = e as { response?: { status?: number; data?: { error?: string } } }
        if (err.response?.status !== 404) {
          showToast('error', `Projekte konnten nicht geladen werden: ${err.response?.data?.error ?? 'Unbekannt'}`)
        }
        setProjects([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [slugOrToken, keycloakApi, showToast])

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Projekte</h1>
          <p className="page-subtitle">
            Status, Fortschritt und Zieltermin Ihrer laufenden Projekte.
          </p>
        </div>
      </div>

      {loading ? (
        <p style={{ color: '#6b7280' }}>Lade …</p>
      ) : unavailable ? (
        <div role="status" style={{ padding: '1.25rem 1rem', background: '#f9fafb', borderRadius: 6, color: '#6b7280', textAlign: 'center' }}>
          Projektdaten sind für {organization.name} derzeit nicht abrufbar.
        </div>
      ) : projects.length === 0 ? (
        <div style={{ padding: '1.25rem 1rem', background: '#f9fafb', borderRadius: 6, color: '#6b7280', textAlign: 'center' }}>
          Noch keine Projekte für {organization.name}.
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '0.75rem 1rem' }}>Projekt</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Status</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Fortschritt</th>
                <th style={{ padding: '0.75rem 1rem' }}>Zieltermin</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.name} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <Link to={encodeURIComponent(p.name)} style={{ color: '#FE4F00', textDecoration: 'none' }}>
                      {p.name}
                    </Link>
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem', color: '#6b7280' }}>
                    {STATUS_LABEL[p.status] ?? p.status}
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden', maxWidth: 120 }}>
                        <div style={{
                          width: `${Math.max(0, Math.min(100, p.percent_complete))}%`,
                          height: '100%',
                          background: '#FE4F00',
                        }} />
                      </div>
                      <span style={{ color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>
                        {Math.round(p.percent_complete)}%
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>
                    {fmtDate(p.expected_end_date)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
